import React, { useEffect, useMemo, useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import {
  Box,
  Grid,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import { Button, GenderSelector, InfoMessage, Modal, Tag, TitleSubtitle, Tooltip, VerificationCodeInput } from "../../../components";
import 'react-phone-input-2/lib/bootstrap.css'
import dayjs from "dayjs";
import { Profile } from "../Profile";
import { Icon } from "../../../icons";
import GoogleMapsAutoComplete from "../../../components/GoogleMapsAutoComplete/GoogleMapsAutoComplete";
import getConfig from "next/config";
import { setGeocoderInfo } from "../../../utils/google-location";
import { DateTextField } from "../../../components/Register/DateOfBirth/DateOfBirth.styles";
import { emailValidation } from "../../../utils/regex";
import { validateBirthDate } from "../../../utils/dates";
import { forgotPasswordAdmin } from "../../../services/AuthenticationService";
import { ProfileInformation, ProfileInformationLabel, ActionLabel, InfoMessageContainer } from "./MainInfo.styles";
import { TextField } from "../../../components";
import { CustomPhoneInput } from "../../../components/Register/PhoneNumber/PhoneNumber.styles";
import { TagContainer } from "../../../components/Register/Accessibility/Accessibility.styles";
import { FieldsContainer, PhoneNumberVerificationContainer, ResendCode, StyledBlueSpan } from "src/components/Register/PhoneNumberVerification/PhoneNumberVerification.styles";
import { confirmAndUpdateEmailViaCode, confirmAndUpdatePhoneNumberViaCode, sendEmailVerificationCode, sendPhoneNumberVerificationCodeWithAuth, updateUserGender } from "src/services/UserService";
import { Modals, useModal } from "src/contexts/ModalContext";
import { accessibilityOptionsMapWithNone } from "src/models/Accessibility";
import { FieldLengths, otherAccessibilityCounter } from "src/models/FieldLengths";

interface MainInfoProps extends WithRouterProps {
  handleSubmit: Function;
  profile?: Profile;
  fetchProfile: Function;
}

const MainInfo: React.FC<MainInfoProps> = ({
  router,
  handleSubmit,
  profile,
  fetchProfile
}) => {
  const { publicRuntimeConfig } = getConfig();
  const [currentlyEditing, setCurrentlyEditing] = useState("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState<boolean>(false);
  const [resetPasswordSnackbarOpen, setResetPasswordSnackbarOpen] = useState<boolean>(false);
  const [resetPasswordError, setResetPasswordError] = useState<boolean>(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState<string>("");
  const [notificationSnackbarOpen, setNotificationSnackbarOpen] = useState<boolean>(false);
  const [notificationSnackbarMessage, setNotificationSnackbarMessage] = useState<string>("");
  const [notificationSnackbarError, setNotificationSnackbarError] = useState<boolean>(false);
  const [verificationCodeHasError, setVerificationCodeHasError] = useState<boolean>(false);
  const [userAlreadyHasAccessibility, setUserAlreadyHasAccessibility] = useState<boolean>(false);

  const [verifyPhoneDisabled, setVerifyPhoneDisabled] = useState<boolean>(true);
  const [verifyEmailDisabled, setVerifyEmailDisabled] = useState<boolean>(true);

  //input field values
  const [firstNameValue, setFirstNameValue] = useState<string | undefined>("");
  const [lastNameValue, setLastNameValue] = useState<string | undefined>("");
  const [emailValue, setEmailValue] = useState<string | undefined>("");
  const [emailValueValidating, setEmailValueValidating] = useState<boolean>(false);
  const [phoneValue, setPhoneValue] = useState<string | null | undefined>("");
  const [phoneValueValidating, setPhoneValueValidating] = useState<boolean>(false);
  const [smsValidationInputValue, setSmsValidationInputValue] = useState<string | null | undefined>("");
  const [dayValue, setDayValue] = useState<number | undefined>();
  const [monthValue, setMonthValue] = useState<number | undefined>();
  const [yearValue, setYearValue] = useState<number | undefined>();
  const [addressValue, setAddressValue] = useState<any>(null);
  const [accessibilityVal, setAccessibilityVal] = React.useState<string | null>(null);
  const [accessibilityIssue, setAccessibilityIssue] = React.useState<string | null | undefined>(null);
  const [genderValue, setGenderValue] = useState<string | null>(null);
  const [genderSaving, setGenderSaving] = useState<boolean>(false);
  const { openModal } = useModal();

  interface EditButtonProps {
    editType: "name" | "email" | "phone" | "birth" | "address" | "accessibility" | "gender" | "",
    disabled?: boolean
  }

  useEffect(() => {
    fillLocalProfileState();
  }, [profile]);

  useEffect(() => {
    validate(currentlyEditing);
  }, [accessibilityVal, accessibilityIssue, firstNameValue, lastNameValue, yearValue, monthValue, dayValue, addressValue])

  const fillLocalProfileState = () => {
    setFirstNameValue(profile?.firstName);
    setLastNameValue(profile?.lastName);
    setEmailValue(profile?.email);
    setPhoneValue(profile?.phoneNumber);
    setDayValue(Number(dayjs.utc(profile?.dateOfBirth).format('DD')));
    setMonthValue(Number(dayjs.utc(profile?.dateOfBirth).format('MM')));
    setYearValue(Number(dayjs.utc(profile?.dateOfBirth).format('YYYY')));
    setAccessibilityVal(profile?.accessibility === null && profile?.otherAccessibility === null
      ? null
      : profile?.accessibility ?? null);
    setAccessibilityIssue(profile?.otherAccessibility ?? null);
    setUserAlreadyHasAccessibility(profile?.accessibility !== undefined && profile.accessibility !== null);
    setGenderValue(profile?.gender ?? null);
  };

  const CancelEditButton = () => {
    return (
      <Button
        gradientStyle="gradient1"
        onClick={() => {
          setCurrentlyEditing("");
          setErrorMsg("");
          fillLocalProfileState();
          setPhoneValueValidating(false);
          setEmailValueValidating(false);
        }}
        size="md"
        variant="tertiary"
        sx={{ padding: 0 }}
      >
        <Stack direction="row">
          <Icon icon="X" size="sm" sx={{ marginRight: '.2rem' }} /> Cancel
        </Stack>
      </Button>
    )
  };

  const EditButton = (props: EditButtonProps) => {
    return (
      <Button
        data-cy={`profile-tab-${props?.editType}-edit`}
        gradientStyle="gradient1"
        onClick={() => {
          setPhoneValueValidating(false);
          setEmailValueValidating(false);
          setCurrentlyEditing(props?.editType);
          setErrorMsg("");
        }}
        size="md"
        variant="tertiary"
        sx={{ padding: 0 }}
        disabled={props?.disabled}
      >
        <Stack direction="row">
          <Icon icon="Pencil" size="sm" sx={{ marginRight: '.2rem' }} /> Edit
        </Stack>
      </Button>
    )
  };

  const UpdateButton = (triggeredFrom: any) => {
    return (
      <Button
        sx={{ margin: '.5rem 0 1.5rem auto' }}
        gradientStyle="gradient1"
        onClick={() => {
          if (validate(currentlyEditing)) {
            if (userAlreadyHasAccessibility) {
              submit();
            } else {
              openModal(Modals.AccessibilityRequirementsChange, () => submit());
            }
          }
        }}
        size="sm"
        variant="primary"
      >
        Update
      </Button>
    )
  };

  const VerifyPhoneButton = (triggeredFrom: any) => {
    return (
      <Button
        data-cy="profile-tab-phone-verify"
        sx={{ margin: '.5rem 0 1.5rem 0' }}
        gradientStyle="gradient1"
        onClick={() => {
          setErrorMsg("");
          handlePhoneUpdate();
        }}
        size="sm"
        variant="primary"
        disabled={verifyPhoneDisabled}
      >
        Verify Number
      </Button>
    )
  };

  const VerifyEmailButton = (triggeredFrom: any) => {
    return (
      <Button
        sx={{ margin: '.5rem 0 1.5rem 0' }}
        gradientStyle="gradient1"
        onClick={() => {
          handleEmailUpdate();
          setErrorMsg("");
        }}
        size="sm"
        variant="primary"
        disabled={verifyEmailDisabled}
      >
        Verify Email
      </Button>
    )
  };

  const onAddressChange = (value: any) => {
    var registerAddress = {
      latitude: null,
      longitude: null,
      longLocation: null,
      shortLocation: null,
      detailsJson: null,
      country: "",
      unitOrApartmentNumber: null,
      street: "",
      suburb: null,
      state: null,
      postCode: ""
    }
    setGeocoderInfo(value, registerAddress);
    setAddressValue(registerAddress);
  }

  const triggerEmailValidation = () => {
    if (emailValue) {
      if (!emailValidation(emailValue)) {
        setErrorMsg("The email you provided is incorrect!");
      } else {
        sendEmailVerificationCode(emailValue).then((res) => {
          if (res === true) {
            setEmailValueValidating(true);
            setNotificationSnackbarError(false);
            setNotificationSnackbarOpen(true);
            setNotificationSnackbarMessage("Nice! Your email with the code is on its way!");
            setErrorMsg("");
          } else {
            setErrorMsg(res?.[0] ? res[0] : "Error! Please try again.");
          }
        });
      }
    } else {
      setErrorMsg("Please provide an email!")
    }
  };

  const triggerPhoneValidation = () => {
    if (phoneValue) {
      const phone = phoneValue.includes("+") ? phoneValue : "+" + phoneValue;
      sendPhoneNumberVerificationCodeWithAuth(phone).then((res) => {
        if (res === true) {
          setPhoneValueValidating(true);
          setNotificationSnackbarError(false);
          setNotificationSnackbarOpen(true);
          setNotificationSnackbarMessage("Nice! Your sms with the code is on its way!");
          setErrorMsg("");
        } else {
          setErrorMsg(res?.[0] ? res[0] : "Error! Please try again.");
        }
      });
    } else {
      setErrorMsg("Please provide a phone number!")
    }
  };

  const handlePhoneUpdate = () => {
    if (phoneValue && smsValidationInputValue) {
      const phone = phoneValue.includes("+") ? phoneValue : "+" + phoneValue;
      confirmAndUpdatePhoneNumberViaCode(phone, smsValidationInputValue).then((res) => {
        if (res === true) {
          setNotificationSnackbarError(false);
          setNotificationSnackbarOpen(true);
          setNotificationSnackbarMessage("Success! We saved your new phone number.");
          setPhoneValueValidating(false);
          setCurrentlyEditing("");
          fetchProfile();
        } else {
          setErrorMsg(res?.[0] ? res[0] : "Error! Please try again.");
        }
      });
    } else {
      setErrorMsg("Please provide a phone number!");
      setPhoneValueValidating(false);
    }
  }

  const handleEmailUpdate = () => {
    if (emailValue) {
      if (!emailValidation(emailValue)) {
        setErrorMsg("The email you provided is incorrect!");
        return
      } else {
        if (smsValidationInputValue) {
          confirmAndUpdateEmailViaCode(emailValue, smsValidationInputValue).then((res) => {
            if (res === true) {
              setNotificationSnackbarError(false);
              setNotificationSnackbarOpen(true);
              setNotificationSnackbarMessage("Success! We saved your new email.");
              setEmailValueValidating(false);
              setCurrentlyEditing("");
              fetchProfile();
            } else {
              setErrorMsg(res?.[0] ? res[0] : "Error! Please try again.");
            }
          });
        }
      }
    } else {
      setErrorMsg("Email is required!");
    }
  }

  const validate = (triggeredFrom: any): boolean => {
    //validation
    if (firstNameValue?.length === 0 || lastNameValue?.length === 0) {
      setErrorMsg("First and last name are required!");
      return false;
    }

    if (!validateBirthDate(yearValue, monthValue, dayValue)) {
      setErrorMsg("The birth date you provided is incorrect!");
      return false;
    }

    if (triggeredFrom === "address") {
      if (!addressValue?.street) {
        setErrorMsg("Street is mandatory!");
        return false;
      }
    }

    if (accessibilityVal === null && accessibilityIssue?.trim() === "") {
      setErrorMsg("You must select one Accessibility Requirement, even if the answer is ‘None’");
      return false;
    } else if (accessibilityVal === "Other" && (!accessibilityIssue || accessibilityIssue?.trim() === "")) {
      setErrorMsg("You must type in a custom Accessibility Requirement, if you selected ‘Other’");
      return false;
    }

    setErrorMsg("");
    return true;
  };

  const submit = () => {
    let birthDate = undefined;
    if (yearValue && monthValue && dayValue) {
      birthDate = new Date(Date.UTC(yearValue, monthValue - 1, dayValue, 0, 0, 0, 0));
    }
    const address = addressValue ? addressValue : profile?.address;

    let profileData: Profile = {
      firstName: firstNameValue,
      lastName: lastNameValue,
      email: emailValue,
      intentionalShortName: true,
      dateOfBirth: birthDate,
      phoneNumber: phoneValue,
      emailPreferences: profile?.emailPreferences,
      accessibility: accessibilityVal === "None"
        ? null
        : accessibilityVal,
      otherAccessibility: accessibilityIssue === ""
        ? null
        : accessibilityIssue?.trim(),
      address: {
        latitude: address.latitude,
        longitude: address.longitude,
        longLocation: address.longLocation,
        shortLocation: address.shortLocation,
        detailsJson: address.detailsJson,
        country: address.country,
        unitOrApartmentNumber: address.unitOrApartmentNumber,
        street: address.street,
        suburb: address.suburb,
        state: address.state,
        postCode: address.postCode,
        createdBy: ""
      },
    };

    handleSubmit(profileData);
    setErrorMsg("");
    setCurrentlyEditing("");
  }

  const handleGenderSave = async () => {
    setGenderSaving(true);
    const success = await updateUserGender(genderValue!);
    setGenderSaving(false);
    setNotificationSnackbarError(!success);
    setNotificationSnackbarOpen(true);
    setNotificationSnackbarMessage(
      success
        ? "Success! Your gender has been updated."
        : "Something went wrong updating your gender. Please try again."
    );
    if (success) {
      setCurrentlyEditing("");
      fetchProfile();
    }
  };

  const handlePasswordReset = () => {
    setResetPasswordLoading(true);
    setResetPasswordSnackbarOpen(false);

    if (profile?.email) {
      forgotPasswordAdmin({ email: profile?.email }).then((res) => {
        setResetPasswordLoading(false);

        if (res.status === 200) {
          setResetPasswordMessage("Your email has been sent.");
          setResetPasswordError(false);
          setResetPasswordSnackbarOpen(true);
        } else if (res.status === 400) {
          setResetPasswordMessage(res.errors?.message?.[0]);
          setResetPasswordError(true);
          setResetPasswordSnackbarOpen(true);
        } else {
          setResetPasswordMessage("Oops! It looks like there's a hiccup. Please try again or contact support if the issue persists.");
          setResetPasswordError(true);
          setResetPasswordSnackbarOpen(true);
        }
      })
    }
  };

  return (
    <div>
      <Box>
        <Grid container spacing={2}>
          <Grid item md={12} marginBottom={10}>
            <Stack
              direction="row"
              justifyContent="space-between"
            >
              <ProfileInformationLabel>
                Name
              </ProfileInformationLabel>

              {currentlyEditing !== "name" && (
                <Tooltip tooltipName="profileFirstNameLock">
                  <EditButton editType="name" disabled={true} />
                </Tooltip>
              )}

              {currentlyEditing === "name" && (
                <CancelEditButton />
              )}
            </Stack>

            <div>
              {currentlyEditing === "name" && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <TextField
                      required
                      value={firstNameValue}
                      onChange={(e) => setFirstNameValue(e.target.value)}
                      placeholder="first name"
                      inputProps={{ maxLength: FieldLengths.user.firstName }}
                      size="sm"
                      fullWidth
                      data-cy="profile-tab-firstName"
                    />

                    <TextField
                      required
                      value={lastNameValue}
                      onChange={(e) => setLastNameValue(e.target.value)}
                      placeholder="last name"
                      inputProps={{ maxLength: FieldLengths.user.lastName }}
                      size="sm"
                      fullWidth
                      data-cy="profile-tab-lastName"
                    />
                  </Stack>

                  {errorMsg && (
                    <InfoMessageContainer>
                      <InfoMessage
                        size="sm"
                        variant="error"
                        hasBackground={true}
                        message={errorMsg}
                      />
                    </InfoMessageContainer>
                  )}

                  <UpdateButton triggeredFrom={"name"} />
                </>
              )}

              {currentlyEditing !== "name" && (
                <Stack
                  direction="row"
                  gap={1}
                  marginBottom={3}
                >
                  <ProfileInformation data-cy="profile-tab-firstName">
                    {profile?.firstName ? profile.firstName : "-"}
                  </ProfileInformation>

                  <ProfileInformation data-cy="profile-tab-lastName">
                    {profile?.lastName ? profile.lastName : "-"}
                  </ProfileInformation>
                </Stack>
              )}
            </div>

            <Stack
              direction="row"
              justifyContent="space-between"
              marginTop={3}
            >
              <ProfileInformationLabel>
                Gender
              </ProfileInformationLabel>

              {currentlyEditing !== "gender" && (
                <EditButton editType="gender" disabled={false} />
              )}

              {currentlyEditing === "gender" && (
                <CancelEditButton />
              )}
            </Stack>

            <div>
              {currentlyEditing === "gender" && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    paddingTop={1}
                  >
                    <GenderSelector
                      value={genderValue}
                      onChange={setGenderValue}
                    />
                  </Stack>

                  <Button
                    sx={{ margin: '.5rem 0 1.5rem auto' }}
                    gradientStyle="gradient1"
                    onClick={handleGenderSave}
                    size="sm"
                    variant="primary"
                    loading={genderSaving}
                    disabled={!genderValue}
                    data-cy="profile-tab-gender-update"
                  >
                    Update
                  </Button>
                </>
              )}

              {currentlyEditing !== "gender" && (
                <Stack
                  direction="row"
                  gap={1}
                  marginBottom={3}
                >
                  <ProfileInformation data-cy="profile-tab-gender">
                    {profile?.gender ? profile.gender : "-"}
                  </ProfileInformation>
                </Stack>
              )}
            </div>

            <Stack
              direction="row"
              justifyContent="space-between"
            >
              <ProfileInformationLabel>
                Email
              </ProfileInformationLabel>

              {currentlyEditing !== "email" && (
                <EditButton editType="email" disabled={false} />
              )}

              {currentlyEditing === "email" && (
                <CancelEditButton />
              )}
            </Stack>

            <div>
              {currentlyEditing === "email" && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <TextField
                      required
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      placeholder="email"
                      size="sm"
                      fullWidth
                      data-cy="profile-tab-email"
                      disabled={emailValueValidating}
                    />
                  </Stack>

                  {emailValueValidating ? (
                    <>
                      <PhoneNumberVerificationContainer>
                        <TitleSubtitle
                          size="sm"
                          subTitle="We just emailed you a code, enter it below to verify email address" />
                        <FieldsContainer>
                          <VerificationCodeInput
                            errorFromOutside={!!errorMsg}
                            inputLength={6}
                            getConcatenatedValues={(value: string) => {
                              setSmsValidationInputValue(value);
                              setVerifyEmailDisabled(value?.length !== 6);
                            }}
                            hasError={(value: boolean) => { setVerificationCodeHasError(value) }} />
                        </FieldsContainer>

                        <ResendCode>Didn’t get it? Check spam folder or <StyledBlueSpan onClick={() => triggerEmailValidation()}>Resend code</StyledBlueSpan></ResendCode>
                      </PhoneNumberVerificationContainer>

                      {errorMsg && (
                        <InfoMessageContainer>
                          <InfoMessage
                            size="sm"
                            variant="error"
                            hasBackground={true}
                            message={errorMsg}
                          />
                        </InfoMessageContainer>
                      )}

                      <VerifyEmailButton triggeredFrom={"email"} />
                    </>
                  ) : (
                    <>
                      {errorMsg && (
                        <InfoMessageContainer>
                          <InfoMessage
                            size="sm"
                            variant="error"
                            hasBackground={true}
                            message={errorMsg}
                          />
                        </InfoMessageContainer>
                      )}

                      <Button
                        sx={{ margin: '.5rem 0 1.5rem auto' }}
                        gradientStyle="gradient1"
                        onClick={() => triggerEmailValidation()}
                        size="sm"
                        variant="primary"
                      >
                        Update
                      </Button>
                    </>
                  )}
                </>
              )}

              {currentlyEditing !== "email" && (
                <Stack
                  direction="row"
                  gap={1}
                  marginBottom={3}
                >
                  <ProfileInformation data-cy="profile-tab-email">
                    {profile?.email ? profile.email : "-"}
                  </ProfileInformation>
                </Stack>
              )}
            </div>


            <Stack
              direction="row"
              justifyContent="space-between"
            >
              <ProfileInformationLabel>
                Phone
              </ProfileInformationLabel>

              {currentlyEditing !== "phone" && (
                <EditButton editType="phone" disabled={false} />
              )}

              {currentlyEditing === "phone" && (
                <CancelEditButton />
              )}
            </Stack>

            <div>
              {currentlyEditing === "phone" && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    gap={2}
                    paddingTop={1}
                    data-cy="profile-tab-phone-input"
                  >
                    <CustomPhoneInput
                      value={phoneValue}
                      onChange={(value) => setPhoneValue(value)}
                      country={"au"}
                      inputProps={{
                        name: 'phone',
                        required: true,
                        autoFocus: true
                      }}
                      disabled={phoneValueValidating}
                    />
                  </Stack>

                  {phoneValueValidating ? (
                    <>
                      <PhoneNumberVerificationContainer>
                        <TitleSubtitle
                          size="sm"
                          subTitle="Enter the code we just sent you via text below" />
                        <FieldsContainer>
                          <VerificationCodeInput
                            errorFromOutside={!!errorMsg}
                            inputLength={6}
                            getConcatenatedValues={(value: string) => {
                              setSmsValidationInputValue(value);
                              setVerifyPhoneDisabled(value?.length !== 6);
                            }}
                            hasError={(value: boolean) => { setVerificationCodeHasError(value) }} />
                        </FieldsContainer>

                        <ResendCode>Didn’t get it? <StyledBlueSpan onClick={() => triggerPhoneValidation()}>Resend code</StyledBlueSpan></ResendCode>
                      </PhoneNumberVerificationContainer>

                      {errorMsg && (
                        <InfoMessageContainer>
                          <InfoMessage
                            data-cy="profile-tab-phone-error"
                            size="sm"
                            variant="error"
                            hasBackground={true}
                            message={errorMsg}
                          />
                        </InfoMessageContainer>
                      )}

                      <VerifyPhoneButton triggeredFrom={"phone"} />
                    </>
                  ) : (
                    <>
                      {errorMsg && (
                        <InfoMessageContainer>
                          <InfoMessage
                            data-cy="profile-tab-phone-error"
                            size="sm"
                            variant="error"
                            hasBackground={true}
                            message={errorMsg}
                          />
                        </InfoMessageContainer>
                      )}

                      <Button
                        data-cy="profile-tab-phone-update"
                        sx={{ margin: '.5rem 0 1.5rem auto' }}
                        gradientStyle="gradient1"
                        onClick={() => triggerPhoneValidation()}
                        size="sm"
                        variant="primary"
                      >
                        Update
                      </Button>
                    </>
                  )}
                </>
              )}

              {currentlyEditing !== "phone" && (
                <Stack
                  direction="row"
                  gap={1}
                  marginBottom={3}
                >
                  <ProfileInformation data-cy="profile-tab-phone">
                    {profile?.phoneNumber ? profile.phoneNumber : "-"}
                  </ProfileInformation>
                </Stack>
              )}
            </div>


            <Stack
              direction="row"
              justifyContent="space-between"
            >
              <ProfileInformationLabel>
                Date of Birth(DD/MM/YY)
              </ProfileInformationLabel>

              {currentlyEditing !== "birth" && (
                <EditButton editType="birth" disabled={false} />
              )}

              {currentlyEditing === "birth" && (
                <CancelEditButton />
              )}
            </Stack>

            <div>
              {currentlyEditing === "birth" && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <DateTextField
                      data-cy="register-date-of-birth-day"
                      placeholder="DD"
                      value={dayValue}
                      onChange={(value) => setDayValue(value)}
                      InputProps={{
                        inputProps: { min: 0 }
                      }}
                    />
                    <DateTextField
                      data-cy="register-date-of-birth-month"
                      placeholder="MM"
                      value={monthValue}
                      onChange={(value) => setMonthValue(value)}
                      InputProps={{
                        inputProps: { min: 0 }
                      }}
                    />
                    <DateTextField
                      data-cy="register-date-of-birth-year"
                      placeholder="YYYY"
                      value={yearValue}
                      onChange={(value) => setYearValue(value)}
                      InputProps={{
                        inputProps: { min: 0 }
                      }}
                    />
                  </Stack>

                  {errorMsg && (
                    <InfoMessageContainer>
                      <InfoMessage
                        size="sm"
                        variant="error"
                        hasBackground={true}
                        message={errorMsg}
                      />
                    </InfoMessageContainer>
                  )}

                  <UpdateButton triggeredFrom={"birth"} />
                </>
              )}

              {currentlyEditing !== "birth" && (
                <Stack
                  direction="row"
                  gap={1}
                  marginBottom={3}
                >
                  <ProfileInformation>
                    {profile?.dateOfBirth ? dayjs.utc(profile.dateOfBirth).format("DD / MM / YY") : "-"}
                  </ProfileInformation>
                </Stack>
              )}
            </div>


            <Stack
              direction="row"
              justifyContent="space-between"
            >
              <ProfileInformationLabel>
                Address
              </ProfileInformationLabel>

              {currentlyEditing !== "address" && (
                <EditButton editType="address" disabled={false} />
              )}

              {currentlyEditing === "address" && (
                <CancelEditButton />
              )}
            </Stack>

            <div>
              {currentlyEditing === "address" && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    gap={2}
                    paddingTop={1}
                  >
                    <GoogleMapsAutoComplete
                      id="google-maps-autocomplete"
                      apiKey={publicRuntimeConfig.googleMapsApiKey}
                      value={addressValue}
                      onChange={onAddressChange}
                      newUIUXStyle={true}
                      data-cy="modal-shootinglocation-location"
                    />
                  </Stack>

                  {errorMsg && (
                    <InfoMessageContainer>
                      <InfoMessage
                        size="sm"
                        variant="error"
                        hasBackground={true}
                        message={errorMsg}
                      />
                    </InfoMessageContainer>
                  )}

                  <UpdateButton triggeredFrom={"address"} />
                </>
              )}

              {currentlyEditing !== "address" && (
                <Stack
                  direction="row"
                  gap={1}
                  marginBottom={3}
                >
                  <ProfileInformation>
                    {profile?.address?.longLocation ? profile?.address?.longLocation : "-"}
                  </ProfileInformation>
                </Stack>
              )}
            </div>

            <Stack
              direction="row"
              justifyContent="space-between"
            >
              <ProfileInformationLabel>
                Accessibility Requirements
              </ProfileInformationLabel>

              {currentlyEditing !== "accessibility" && (
                <EditButton editType="accessibility" disabled={false} />
              )}

              {currentlyEditing === "accessibility" && (
                <CancelEditButton />
              )}
            </Stack>

            <div>
              {currentlyEditing === "accessibility" && (
                <>
                  <Stack
                    direction="column"
                    justifyContent="space-between"
                    gap={2}
                    paddingTop={1}
                  >
                    <TagContainer>
                      {[...accessibilityOptionsMapWithNone].map(([key, value]) => (
                        <Tag
                          key={`option-${key}`}
                          label={value}
                          isActive={accessibilityVal === key}
                          isControlled
                          onClick={() => {
                            setAccessibilityVal(key)
                            setAccessibilityIssue("")
                          }}
                          onDelete={() => {
                            setAccessibilityVal(null)
                          }}
                        />
                      ))}
                    </TagContainer>
                    <TextField
                      fullWidth
                      label="Other"
                      placeholder="Please Specify"
                      value={accessibilityIssue ?? ""}
                      inputProps={{ maxLength: FieldLengths.user.otherAccessibility }}
                      helperText={otherAccessibilityCounter(accessibilityIssue)}
                      onChange={(
                        event: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >
                      ) => {
                        setAccessibilityIssue(event.target.value)
                        setAccessibilityVal("Other")
                      }
                      }
                    />
                  </Stack>

                  {errorMsg && (
                    <InfoMessageContainer>
                      <InfoMessage
                        size="sm"
                        variant="error"
                        hasBackground={true}
                        message={errorMsg}
                      />
                    </InfoMessageContainer>
                  )}

                  <UpdateButton triggeredFrom={"accessibility"} />
                </>
              )}

              {currentlyEditing !== "accessibility" && (
                <Stack
                  direction="row"
                  gap={1}
                >
                  <ProfileInformation>
                    {accessibilityIssue && accessibilityIssue != "" ? accessibilityIssue : accessibilityOptionsMapWithNone.get(accessibilityVal ?? "None") ?? "-"}
                  </ProfileInformation>
                </Stack>
              )}
            </div>
          </Grid>


          <Grid item md={12} marginBottom={10}>
            <Stack
              direction="row"
              justifyContent="space-between"
            >
              <ActionLabel>
                Change Password?
              </ActionLabel>

              <Button
                gradientStyle="gradient1"
                onClick={() => handlePasswordReset()}
                size="md"
                variant="tertiary"
                sx={{ padding: 0 }}
                loading={resetPasswordLoading}
              >
                <Stack direction="row">
                  <Icon icon="Mail" size="sm" sx={{ marginRight: '.2rem' }} /> Send Reset Email
                </Stack>
              </Button>
            </Stack>

            {/* -- removed until delete account is fixed -- */}

            {/* <Stack
              marginTop={1}
              direction="row"
              justifyContent="space-between"
            >
              <ActionLabel>
                Permanently Delete Your Account
              </ActionLabel>

              <Button
                gradientStyle="gradient1"
                onClick={() => console.log("delete account")}
                size="md"
                variant="tertiary"
                sx={{padding: 0}}
              >
                <Stack direction="row">
                  <Icon icon="Trash" size="sm" sx={{marginRight: '.2rem'}} /> Delete Account
                </Stack>
              </Button>
            </Stack> */}
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={resetPasswordSnackbarOpen} autoHideDuration={6000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} onClose={() => setResetPasswordSnackbarOpen(false)}>
        <Alert onClose={() => setResetPasswordSnackbarOpen(false)} severity={resetPasswordError ? 'error' : 'success'} sx={{ width: '100%' }}>
          {resetPasswordMessage}
        </Alert>
      </Snackbar>

      <Snackbar open={notificationSnackbarOpen} autoHideDuration={6000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} onClose={() => setNotificationSnackbarOpen(false)}>
        <Alert onClose={() => setNotificationSnackbarOpen(false)} severity={notificationSnackbarError ? 'error' : 'success'} sx={{ width: '100%' }}>
          {notificationSnackbarMessage}
        </Alert>
      </Snackbar>

      <Modal />
    </div>
  );
};

export default withRouter(MainInfo);
